// Pure adapter helpers for the OR Overview tab. Translate backend
// Canopus types into the `room` / `event` / `case` shape that the mockup
// port expects. Kept in their own module so they can be tested without
// pulling in the monolithic tab file.

import type {
  CanopusActiveCase,
  CanopusCaseLogEntry,
  CanopusEvent,
  CanopusPhaseTiming,
  CanopusRoomStatus,
  CanopusStep,
} from '../../../services/canopusCommand.js';

export function fmtDur(sec: number | null | undefined): string | null {
  if (sec == null || sec < 0 || !isFinite(sec)) return null;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m.toString().padStart(2, '0')}m` : `${m}m`;
}

export function fmtClock(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

// Date + time, e.g. "Mar 5, 12:34 PM". Used for fields where the day
// matters (overnight cases, last interaction) — fmtClock alone is
// ambiguous once a case spans midnight.
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${date}, ${time}`;
  } catch {
    return '';
  }
}

export function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

export interface AdaptedPhase {
  p: string;
  dur: string;
  avg: string;
  d: string;
}

export interface AdaptedStep {
  name: string;
  phase: number;
  done: boolean;
  time: string;
  active: boolean;
}

export interface AdaptedRoom {
  id: string;
  name: string;
  status: CanopusRoomStatus['status'];
  procedure: string | null;
  surgeon: string | null;
  lastCase?: string | null;
  doneAt?: string | undefined;
  idleFor?: string | undefined;
  alerts: string[];
  profile?: string | null;
  specialty?: string | null;
  scrub?: string | null;
  circ?: string | null;
  device?: string | null;
  started?: string;
  elapsed?: string | null;
  pi?: number;
  pct?: number;
  hasAvg?: boolean;
  caseCount?: number;
  avgDur?: string;
  estRem?: string;
  pace?: CanopusActiveCase['pace'];
  paceText?: string | null;
  expectedAdj?: string | undefined;
  paceAdj?: string | undefined;
  totalSteps?: number;
  currentStep?: number;
  currentStepName?: string | null;
  phases?: AdaptedPhase[];
  steps?: AdaptedStep[];
  tfi?: number | null;
  trackingStale?: boolean;
  // Pre-formatted "date, time · relative" for the Last Interaction row in
  // the detail drawer. Falls back to '--' when the backend has no
  // `lastActivityIso`.
  lastInteraction?: string;
}

export interface AdaptedEvent {
  sev: 'info' | 'high' | 'medium' | 'low';
  msg: string;
  t: string;
}

export interface AdaptedCase {
  o: string;
  s: string;
  p: string;
  dur: string;
  avg: string;
  n: number;
  d: string;
  st: 'active' | 'done' | 'long' | 'closing' | 'new';
}

export function adaptRoom(backendRoom: CanopusRoomStatus): AdaptedRoom {
  const ac = backendRoom.activeCase;
  if (!ac) {
    const lastLabel = backendRoom.lastCase
      ? `${backendRoom.lastCase.procedureName} · ${backendRoom.lastCase.surgeonName}`
      : null;
    return {
      id: backendRoom.id,
      name: backendRoom.name,
      status: backendRoom.status,
      procedure: null,
      surgeon: null,
      lastCase: lastLabel,
      doneAt: backendRoom.lastCase ? fmtClock(backendRoom.lastCase.endTimeIso) : undefined,
      idleFor: backendRoom.lastCase ? fmtRelative(backendRoom.lastCase.endTimeIso) : undefined,
      alerts: [],
    };
  }

  const paceText =
    ac.pace === 'on'
      ? 'On pace'
      : ac.pace === 'over' && ac.avgDurationSec != null
        ? `+${Math.round((ac.elapsedSec - ac.avgDurationSec) / 60)} min`
        : ac.pace === 'slight' && ac.avgDurationSec != null
          ? `+${Math.round((ac.elapsedSec - ac.avgDurationSec) / 60)} min`
          : ac.pace === 'new'
            ? `${ac.caseCount}/15 cases`
            : null;

  return {
    id: backendRoom.id,
    name: backendRoom.name,
    status: backendRoom.status,
    surgeon: ac.surgeonName || null,
    procedure: ac.procedureName || null,
    profile: ac.profileName || null,
    specialty: ac.specialty || null,
    scrub: ac.scrubTechName ?? null,
    circ: null,
    device: ac.deviceName ?? null,
    tfi: ac.tfi,
    started: fmtDateTime(ac.startTimeIso),
    elapsed: fmtDur(ac.elapsedSec),
    pi: ac.currentPhaseIndex ?? 0,
    pct: ac.totalSteps ? Math.round((ac.stepsCompleted / ac.totalSteps) * 100) : 0,
    hasAvg: ac.hasAvg,
    caseCount: ac.caseCount,
    avgDur: fmtDur(ac.avgDurationSec) || '--',
    estRem: fmtDur(ac.estRemainingSec) || '--',
    pace: ac.pace,
    paceText,
    expectedAdj: undefined,
    paceAdj: undefined,
    totalSteps: ac.totalSteps,
    currentStep: ac.currentStepIndex,
    currentStepName: ac.currentStepName,
    phases: ac.phases.map(
      (p: CanopusPhaseTiming): AdaptedPhase => ({
        p: p.name,
        dur: fmtDur(p.durationSec) ?? (p.inProgress ? 'In progress' : '--'),
        avg: fmtDur(p.avgSec) ?? '--',
        d: p.inProgress ? 'In progress' : '',
      })
    ),
    steps: ac.steps.map(
      (s: CanopusStep): AdaptedStep => ({
        name: s.name,
        phase: s.phaseIndex,
        done: s.done,
        time: s.clickTimeIso ? fmtClock(s.clickTimeIso) : '',
        active: !s.done && s.name === ac.currentStepName,
      })
    ),
    alerts: ac.alerts ?? [],
    trackingStale: ac.trackingStale ?? false,
    lastInteraction: ac.lastActivityIso
      ? `${fmtDateTime(ac.lastActivityIso)} · ${fmtRelative(ac.lastActivityIso)}`
      : '--',
  };
}

export function adaptEvent(e: CanopusEvent): AdaptedEvent {
  return {
    sev: 'info',
    msg: `${e.roomName}: ${e.stepName}`,
    t: `${fmtClock(e.timestampIso)} · ${fmtRelative(e.timestampIso)}`,
  };
}

export function adaptCase(c: CanopusCaseLogEntry): AdaptedCase {
  const isActive = !c.endTimeIso;
  return {
    o: c.roomName,
    s: c.surgeonName,
    p: c.procedureName,
    // Show elapsed time for both active (running clock) and completed cases.
    // Fall back to '--' only when no duration is available (e.g. a freshly
    // started case before any elapsed seconds are recorded).
    dur: c.totalTimeSec > 0 ? (fmtDur(c.totalTimeSec) ?? '--') : '--',
    avg: fmtDur(c.avgDurationSec) ?? '--',
    n: c.caseCount,
    d: isActive ? 'In progress' : 'Completed',
    st: isActive ? 'active' : 'done',
  };
}
